import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog as UIDialog, DialogContent as UIDialogContent, DialogHeader as UIDialogHeader, DialogTitle as UIDialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { db, auth } from "@/lib/firebase";
import { collection, getDocs, query, where, addDoc, onSnapshot, doc, getDoc, Timestamp, updateDoc } from "firebase/firestore";
import { UserPlus, Users, GraduationCap, Calendar, Search, Clock } from "lucide-react";
import MessageDialog from "@/components/ui/MessageDialog";

import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";

interface WalkInStudent {
  id?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  email?: string;
  address?: string;
  dateOfBirth?: string;
  gender?: string;
  branch: string;
  registrationType: "walk-in";
  createdAt: any;
  createdBy: string; // staff member who created the record
  ltoClientNumber?: string;
  maritalStatus?: string;
  licenseNumber?: string;
}

const StaffWalkInStudents: React.FC = () => {
  const [walkInStudents, setWalkInStudents] = useState<WalkInStudent[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [userBranch, setUserBranch] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<WalkInStudent | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null);
  
  const [newStudent, setNewStudent] = useState<Partial<WalkInStudent>>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    dateOfBirth: "",
    gender: "",
  });
  
  const [enrollmentData, setEnrollmentData] = useState({
    courseId: "",
    paymentMethod: "cash",
    paymentAmount: 0,
    notes: "",
  });
  
  const [scheduleData, setScheduleData] = useState({
    timeSlotId: "",
    date: "",
    instructorId: "",
    notes: "",
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: "cash",
    notes: "",
  });
  
  const [creating, setCreating] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [message, setMessage] = useState<{ open: boolean; text: string; type?: "success" | "error" | "info" }>({ 
    open: false, text: "" 
  });

  useEffect(() => {
    const setupData = async () => {
      if (!auth.currentUser) return;
      
      try {
        // Get staff user's branch
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        const userData = userDoc.data();
        const branch = userData?.branch || "";
        setUserBranch(branch);

        // Set up real-time listeners
        const unsubscribers: (() => void)[] = [];

        // Listen to walk-in students for this branch
        const walkInStudentsQuery = query(
          collection(db, "users"), 
          where("registrationType", "==", "walk-in"),
          where("branch", "==", branch)
        );
        const walkInStudentsUnsubscribe = onSnapshot(walkInStudentsQuery, (snapshot) => {
          const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as WalkInStudent[];
          setWalkInStudents(students);
        });
        unsubscribers.push(walkInStudentsUnsubscribe);

        // Listen to courses
        const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
          setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(coursesUnsubscribe);

        // Listen to branches
        const branchesUnsubscribe = onSnapshot(collection(db, "branches"), (snapshot) => {
          setBranches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(branchesUnsubscribe);

        // Listen to enrollments for this branch
        const enrollmentsQuery = query(
          collection(db, "enrollments"), 
          where("branch", "==", branch)
        );
        const enrollmentsUnsubscribe = onSnapshot(enrollmentsQuery, (snapshot) => {
          setEnrollments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(enrollmentsUnsubscribe);

        // Listen to time slots for this branch
        const timeSlotsQuery = query(
          collection(db, "timeSlots"), 
          where("branch", "==", branch)
        );
        const timeSlotsUnsubscribe = onSnapshot(timeSlotsQuery, (snapshot) => {
          setTimeSlots(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(timeSlotsUnsubscribe);

        // Listen to instructors
        const instructorsQuery = query(
          collection(db, "users"), 
          where("role", "==", "instructor")
        );
        const instructorsUnsubscribe = onSnapshot(instructorsQuery, (snapshot) => {
          setInstructors(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(instructorsUnsubscribe);

        // Listen to schedules for this branch
        const schedulesQuery = query(
          collection(db, "schedules"), 
          where("branch", "==", branch)
        );
        const schedulesUnsubscribe = onSnapshot(schedulesQuery, (snapshot) => {
          setSchedules(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(schedulesUnsubscribe);

        setLoading(false);

        // Cleanup function
        return () => {
          unsubscribers.forEach(unsubscribe => unsubscribe());
        };
      } catch (error) {
        console.error("Error setting up walk-in students data:", error);
        setMessage({ open: true, text: "Failed to load data.", type: "error" });
        setLoading(false);
      }
    };

    setupData();
  }, []);

  const handleCreateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newStudent.firstName?.trim() || !newStudent.lastName?.trim() || !newStudent.phone?.trim()) {
      setMessage({ open: true, text: "First name, last name, and phone are required.", type: "error" });
      return;
    }

    setCreating(true);
    try {
      // Create unique student ID for walk-in students
      const studentId = `walkin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const studentData = {
        ...newStudent,
        branch: userBranch,
        registrationType: "walk-in" as const,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser!.uid,
        role: "student",
        uid: studentId, // Custom UID for walk-in students
      };

      await addDoc(collection(db, "users"), studentData);

      setMessage({ open: true, text: "Walk-in student created successfully!", type: "success" });
      setShowCreateDialog(false);
      setNewStudent({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        dateOfBirth: "",
        gender: "",
      });
    } catch (error) {
      console.error("Error creating walk-in student:", error);
      setMessage({ open: true, text: "Failed to create student.", type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleEnrollStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!enrollmentData.courseId || !selectedStudent) {
      setMessage({ open: true, text: "Please select a course.", type: "error" });
      return;
    }

    // Check if student is already enrolled in this course
    const existingEnrollment = enrollments.find(
      e => e.studentId === selectedStudent.id && e.courseId === enrollmentData.courseId
    );
    
    if (existingEnrollment) {
      setMessage({ open: true, text: "Student is already enrolled in this course.", type: "info" });
      return;
    }

    setEnrolling(true);
    try {
      const selectedCourse = courses.find(c => c.id === enrollmentData.courseId);
      const courseFee = selectedCourse?.price || 0;
      const amountReceived = enrollmentData.paymentAmount || 0;
      const balance = courseFee - amountReceived;

      await addDoc(collection(db, "enrollments"), {
        studentId: selectedStudent.id,
        courseId: enrollmentData.courseId,
        branch: userBranch,
        courseFee,
        amountReceived,
        balance,
        status: balance <= 0 ? "approved" : "partial_payment", // Approved if fully paid, partial if balance remains
        enrolledAt: Timestamp.now(),
        paymentMethod: enrollmentData.paymentMethod,
        paymentReceived: amountReceived > 0,
        notes: enrollmentData.notes,
        registrationType: "walk-in",
        enrolledBy: auth.currentUser!.uid, // Track which staff member enrolled them
      });

      setMessage({ open: true, text: "Student enrolled successfully!", type: "success" });
      setShowEnrollDialog(false);
      setSelectedStudent(null);
      setEnrollmentData({
        courseId: "",
        paymentMethod: "cash",
        paymentAmount: 0,
        notes: "",
      });
    } catch (error) {
      console.error("Error enrolling student:", error);
      setMessage({ open: true, text: "Failed to enroll student.", type: "error" });
    } finally {
      setEnrolling(false);
    }
  };

  const handleScheduleStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!scheduleData.timeSlotId || !scheduleData.date || !scheduleData.instructorId || !selectedEnrollment) {
      setMessage({ open: true, text: "Please fill in all required fields.", type: "error" });
      return;
    }

    // Check if slot has reached maximum capacity
    const schedulesForSlot = schedules.filter(schedule => 
      schedule.timeSlotId === scheduleData.timeSlotId && 
      schedule.date === scheduleData.date
    );
    
    const timeSlot = timeSlots.find(ts => ts.id === scheduleData.timeSlotId);
    const maxStudents = timeSlot?.maxStudents || 1;
    
    if (schedulesForSlot.length >= maxStudents) {
      setMessage({ open: true, text: "This time slot is at maximum capacity. Please select a different time slot.", type: "error" });
      return;
    }

    setScheduling(true);
    try {
      await addDoc(collection(db, "schedules"), {
        studentId: selectedEnrollment.studentId,
        enrollmentId: selectedEnrollment.id,
        courseId: selectedEnrollment.courseId,
        timeSlotId: scheduleData.timeSlotId,
        instructorId: scheduleData.instructorId,
        date: scheduleData.date,
        time: `${timeSlot?.startTime} - ${timeSlot?.endTime}`,
        status: "scheduled",
        scheduledAt: Timestamp.now(),
        scheduledBy: auth.currentUser!.uid, // Track which staff member scheduled
        notes: scheduleData.notes,
        registrationType: "walk-in",
        branch: userBranch,
      });

      setMessage({ open: true, text: "Session scheduled successfully!", type: "success" });
      setShowScheduleDialog(false);
      setSelectedEnrollment(null);
      setScheduleData({
        timeSlotId: "",
        date: "",
        instructorId: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error scheduling session:", error);
      setMessage({ open: true, text: "Failed to schedule session.", type: "error" });
    } finally {
      setScheduling(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || paymentData.amount <= 0) {
      setMessage({ open: true, text: "Please enter a valid payment amount.", type: "error" });
      return;
    }

    setProcessingPayment(true);
    try {
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      const newAmountReceived = selectedEnrollment.amountReceived + paymentData.amount;
      const newBalance = Math.max(0, selectedEnrollment.courseFee - newAmountReceived);
      const newStatus = newBalance <= 0 ? "approved" : "partial_payment";

      await updateDoc(enrollmentRef, {
        amountReceived: newAmountReceived,
        balance: newBalance,
        status: newStatus,
        lastPaymentAt: Timestamp.now(),
        lastPaymentAmount: paymentData.amount,
        lastPaymentMethod: paymentData.paymentMethod,
        lastPaymentNotes: paymentData.notes,
      });

      setMessage({ open: true, text: "Payment recorded successfully!", type: "success" });
      setShowPaymentDialog(false);
      setSelectedEnrollment(null);
      setPaymentData({
        amount: 0,
        paymentMethod: "cash",
        notes: "",
      });
    } catch (error) {
      console.error("Error recording payment:", error);
      setMessage({ open: true, text: "Failed to record payment.", type: "error" });
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatTime12Hour = (time24: string) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const filteredStudents = walkInStudents.filter(student =>
    `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.middleName && student.middleName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    student.phone.includes(searchTerm) ||
    (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.ltoClientNumber && student.ltoClientNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (student.licenseNumber && student.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStudentEnrollments = (studentId: string) => {
    return enrollments.filter(e => e.studentId === studentId);
  };

  const getStudentSchedules = (studentId: string) => {
    return schedules.filter(s => s.studentId === studentId);
  };

  const getEnrollmentProgress = (enrollment: any) => {
    const course = courses.find(c => c.id === enrollment.courseId);
    const completedSessions = schedules.filter(
      s => s.enrollmentId === enrollment.id && s.status === "completed"
    );
    
    if (!course || !course.totalHours) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    
    const completedHours = completedSessions.reduce((total, session) => {
      const timeSlot = timeSlots.find(ts => ts.id === session.timeSlotId);
      if (timeSlot && timeSlot.startTime && timeSlot.endTime) {
        const [startHour, startMin] = timeSlot.startTime.split(':').map(Number);
        const [endHour, endMin] = timeSlot.endTime.split(':').map(Number);
        const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
        return total + (duration / 60);
      }
      return total + 4; // fallback to 4 hours if time slot not found
    }, 0);
    
    const percentage = Math.min(100, Math.round((completedHours / course.totalHours) * 100));
    
    return {
      completed: completedHours,
      total: course.totalHours,
      percentage
    };
  };

  // Get available time slots for a specific date by querying the schedules collection
  const getAvailableTimeSlotsForDate = (selectedDate: string) => {
    if (!selectedDate) return [];
    
    // Get day of week from the selected date
    const dayOfWeek = format(new Date(selectedDate), 'EEEE').toLowerCase();
    
    // Get all time slots for this day of week
    const slotsForDay = timeSlots.filter(slot => slot.dayOfWeek === dayOfWeek);
    
    // Query schedules collection for this specific date
    const schedulesForDate = schedules.filter(schedule => schedule.date === selectedDate);
    
    // Filter out slots that have reached their maximum capacity
    const availableSlots = slotsForDay.filter(slot => {
      const schedulesForSlot = schedulesForDate.filter(schedule => schedule.timeSlotId === slot.id);
      return schedulesForSlot.length < slot.maxStudents;
    });
    
    return availableSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading walk-in students...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Walk-in Students</h2>
          <p className="text-muted-foreground">Manage students who registered in person</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Walk-in Student
        </Button>
      </div>

      {/* Search and Stats */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Badge variant="outline">
            <Users className="h-3 w-3 mr-1" />
            {filteredStudents.length} Students
          </Badge>
        </div>
      </div>

      {/* Students List */}
      <div className="grid gap-4">
        {filteredStudents.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No walk-in students found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "Try adjusting your search criteria." : "Create your first walk-in student record."}
              </p>
              {!searchTerm && (
                <Button onClick={() => setShowCreateDialog(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Walk-in Student
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredStudents.map((student) => {
            const studentEnrollments = getStudentEnrollments(student.id!);
            const studentSchedules = getStudentSchedules(student.id!);
            const hasApprovedEnrollments = studentEnrollments.some(e => e.status === "approved");
            const hasPartialPayments = studentEnrollments.some(e => e.status === "partial_payment");
            const hasBalanceOwed = studentEnrollments.some(e => {
              const balance = Number(e.balance) || 0;
              return balance > 0;
            });
            
            return (
              <Card key={student.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium">
                        {student.firstName} {student.middleName ? `${student.middleName} ` : ''}{student.lastName}
                      </h3>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div>📞 {student.phone}</div>
                        {student.email && <div>✉️ {student.email}</div>}
                        {student.address && <div>📍 {student.address}</div>}
                        {student.ltoClientNumber && <div>🆔 LTO: {student.ltoClientNumber}</div>}
                        {student.maritalStatus && <div>💍 Status: {student.maritalStatus}</div>}
                        {student.licenseNumber && <div>🎫 License: {student.licenseNumber}</div>}
                        <div>📅 Created: {student.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</div>
                      </div>
                      
                      {/* Enrollments Summary */}
                      <div className="space-y-2 mt-3">
                        <div className="flex gap-2 flex-wrap">
                          <Badge variant="outline">
                            <GraduationCap className="h-3 w-3 mr-1" />
                            {studentEnrollments.length} Course{studentEnrollments.length !== 1 ? 's' : ''}
                          </Badge>
                          {studentSchedules.length > 0 && (
                            <Badge variant="outline">
                              <Clock className="h-3 w-3 mr-1" />
                              {studentSchedules.length} Session{studentSchedules.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Course Progress */}
                        {studentEnrollments.map(enrollment => {
                          const course = courses.find(c => c.id === enrollment.courseId);
                          const hasBalance = enrollment.balance && enrollment.balance > 0;
                          const progress = getEnrollmentProgress(enrollment);
                          
                          return (
                            <div key={enrollment.id} className="border rounded p-2 text-xs space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{course?.title || 'Unknown Course'}</span>
                                <div className="flex gap-1">
                                  <Badge variant={enrollment.status === "approved" ? "default" : enrollment.status === "partial_payment" ? "destructive" : "secondary"} className="text-xs">
                                    {enrollment.status === "partial_payment" ? "Partial Payment" : enrollment.status}
                                  </Badge>
                                  {hasBalance && (
                                    <Badge variant="destructive" className="text-xs">
                                      ₱{enrollment.balance} balance
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {enrollment.status === "approved" && progress.total > 0 && (
                                <div className="space-y-1">
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Progress: {progress.completed}h / {progress.total}h</span>
                                    <span>{progress.percentage}%</span>
                                  </div>
                                  <Progress value={progress.percentage} className="h-1" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowEnrollDialog(true);
                        }}
                      >
                        <GraduationCap className="h-4 w-4 mr-1" />
                        Enroll
                      </Button>
                      {hasApprovedEnrollments && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            // Find an approved enrollment for scheduling
                            const approvedEnrollment = studentEnrollments.find(e => e.status === "approved");
                            setSelectedEnrollment(approvedEnrollment);
                            setShowScheduleDialog(true);
                          }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Schedule
                        </Button>
                      )}
                      {(hasBalanceOwed || true) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            console.log('Button clicked for student:', student.firstName, student.lastName);
                            console.log('Student enrollments:', studentEnrollments);
                            console.log('Has balance owed:', hasBalanceOwed);
                            setSelectedStudent(student);
                            const enrollmentWithBalance = studentEnrollments.find(e => {
                              const balance = Number(e.balance) || 0;
                              console.log('Checking enrollment balance:', balance, 'for enrollment:', e);
                              return balance > 0;
                            });
                            console.log('Found enrollment with balance:', enrollmentWithBalance);
                            setSelectedEnrollment(enrollmentWithBalance);
                            setPaymentData({ ...paymentData, amount: Number(enrollmentWithBalance?.balance) || 0 });
                            setShowPaymentDialog(true);
                          }}
                        >
                          💰 Add Payment (Debug)
                        </Button>
                      )}
                      {studentSchedules.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowSessionsDialog(true);
                          }}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          View Sessions
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Student Dialog */}
      <UIDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <UIDialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <UIDialogHeader>
            <UIDialogTitle>Add Walk-in Student</UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleCreateStudent} className="flex-1 flex flex-col">
            <div className="space-y-4 flex-1 overflow-y-auto max-h-[60vh] pr-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newStudent.firstName || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newStudent.lastName || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="middleName">Middle Name</Label>
              <Input
                id="middleName"
                value={newStudent.middleName || ""}
                onChange={(e) => setNewStudent({ ...newStudent, middleName: e.target.value })}
                placeholder="Enter middle name"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={newStudent.phone || ""}
                onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                placeholder="09XXXXXXXXX"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                value={newStudent.email || ""}
                onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                placeholder="student@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="address">Address (Optional)</Label>
              <Input
                id="address"
                value={newStudent.address || ""}
                onChange={(e) => setNewStudent({ ...newStudent, address: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="ltoClientNumber">LTO Client Number</Label>
              <Input
                id="ltoClientNumber"
                value={newStudent.ltoClientNumber || ""}
                onChange={(e) => setNewStudent({ ...newStudent, ltoClientNumber: e.target.value })}
                placeholder="Enter LTO client number"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dateOfBirth">Date of Birth (Optional)</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={newStudent.dateOfBirth || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, dateOfBirth: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="gender">Gender (Optional)</Label>
                <select
                  id="gender"
                  value={newStudent.gender || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, gender: e.target.value })}
                  className="w-full border rounded p-2"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="maritalStatus">Marital Status</Label>
                <select
                  id="maritalStatus"
                  value={newStudent.maritalStatus || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, maritalStatus: e.target.value })}
                  className="w-full border rounded p-2"
                >
                  <option value="">Select marital status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
              <div>
                <Label htmlFor="licenseNumber">Student/Driver License Number</Label>
                <Input
                  id="licenseNumber"
                  value={newStudent.licenseNumber || ""}
                  onChange={(e) => setNewStudent({ ...newStudent, licenseNumber: e.target.value })}
                  placeholder="Enter license number (if applicable)"
                />
              </div>
            </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? "Creating..." : "Create Student"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Enroll Student Dialog */}
      <UIDialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Enroll {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleEnrollStudent} className="space-y-4">
            <div>
              <Label htmlFor="courseId">Course *</Label>
              <select
                id="courseId"
                value={enrollmentData.courseId}
                onChange={(e) => {
                  const courseId = e.target.value;
                  const course = courses.find(c => c.id === courseId);
                  setEnrollmentData({ 
                    ...enrollmentData, 
                    courseId,
                    paymentAmount: course?.price || 0
                  });
                }}
                className="w-full border rounded p-2"
                required
              >
                <option value="">Select a course</option>
                {courses
                  .filter(course => {
                    // Filter courses to show only those available for the staff member's branch
                    // If course has no branches array, show it (for backward compatibility)
                    // If course has branches array, show it only if staff's branch is included
                    return !course.branches || course.branches.length === 0 || course.branches.includes(userBranch);
                  })
                  .map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title} - ₱{course.price}
                    </option>
                  ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="paymentMethod">Payment Method</Label>
              <select
                id="paymentMethod"
                value={enrollmentData.paymentMethod}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, paymentMethod: e.target.value })}
                className="w-full border rounded p-2"
              >
                <option value="cash">Cash</option>
                <option value="gcash">GCash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="paymentAmount">Amount Received</Label>
              <Input
                id="paymentAmount"
                type="number"
                value={enrollmentData.paymentAmount}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, paymentAmount: Number(e.target.value) })}
                min="0"
                step="0.01"
              />
              {enrollmentData.courseId && (
                <div className="text-sm text-muted-foreground mt-1">
                  Course Fee: ₱{courses.find(c => c.id === enrollmentData.courseId)?.price || 0} | 
                  Balance: ₱{Math.max(0, (courses.find(c => c.id === enrollmentData.courseId)?.price || 0) - enrollmentData.paymentAmount)}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                value={enrollmentData.notes}
                onChange={(e) => setEnrollmentData({ ...enrollmentData, notes: e.target.value })}
                placeholder="Additional notes about this enrollment"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEnrollDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={enrolling}>
                {enrolling ? "Enrolling..." : "Enroll Student"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Schedule Student Dialog */}
      <UIDialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Schedule Session for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <form onSubmit={handleScheduleStudent} className="space-y-4">
            {selectedEnrollment && (
              <div className="p-3 bg-muted rounded">
                <div className="text-sm">
                  <strong>Course:</strong> {courses.find(c => c.id === selectedEnrollment.courseId)?.title || 'Unknown'}
                </div>
                {selectedEnrollment.balance > 0 && (
                  <div className="text-sm text-destructive">
                    <strong>Balance:</strong> ₱{selectedEnrollment.balance}
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="scheduleDate">Date *</Label>
              <Input
                id="scheduleDate"
                type="date"
                value={scheduleData.date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  console.log("WALK-IN: Date selected:", selectedDate);
                  setScheduleData({ ...scheduleData, date: selectedDate, timeSlotId: "" });
                }}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            
            {scheduleData.date && scheduleData.date.length >= 10 && (
              <div>
                <Label htmlFor="timeSlotId">Available Time Slots *</Label>
                <select
                  id="timeSlotId"
                  value={scheduleData.timeSlotId}
                  onChange={(e) => setScheduleData({ ...scheduleData, timeSlotId: e.target.value })}
                  className="w-full border rounded p-2"
                  required
                >
                  <option value="">Select a time slot</option>
                  {(() => {
                    const availableSlots = getAvailableTimeSlotsForDate(scheduleData.date);
                    console.log("WALK-IN: Available slots for", scheduleData.date, ":", availableSlots.length);
                    
                    if (availableSlots.length === 0) {
                      return <option disabled>No available time slots for this day</option>;
                    }
                    
                    return availableSlots.map(slot => (
                      <option key={slot.id} value={slot.id}>
                        {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                      </option>
                    ));
                  })()}
                </select>
                {timeSlots.filter(slot => 
                  slot.dayOfWeek === format(new Date(scheduleData.date), 'EEEE').toLowerCase()
                ).length === 0 && (
                  <div className="text-sm text-muted-foreground mt-1">
                    No time slots configured for {format(new Date(scheduleData.date), 'EEEE')}s
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="instructorId">Instructor *</Label>
              <select
                id="instructorId"
                value={scheduleData.instructorId}
                onChange={(e) => setScheduleData({ ...scheduleData, instructorId: e.target.value })}
                className="w-full border rounded p-2"
                required
              >
                <option value="">Select an instructor</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.firstName} {instructor.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="scheduleNotes">Notes (Optional)</Label>
              <Input
                id="scheduleNotes"
                value={scheduleData.notes}
                onChange={(e) => setScheduleData({ ...scheduleData, notes: e.target.value })}
                placeholder="Additional notes for this session"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={scheduling}>
                {scheduling ? "Scheduling..." : "Schedule Session"}
              </Button>
            </div>
          </form>
        </UIDialogContent>
      </UIDialog>

      {/* Sessions Management Dialog */}
      <UIDialog open={showSessionsDialog} onOpenChange={setShowSessionsDialog}>
        <UIDialogContent className="max-w-2xl">
          <UIDialogHeader>
            <UIDialogTitle>
              Sessions for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          <div className="space-y-4">
            {selectedStudent && (() => {
              const studentSchedules = getStudentSchedules(selectedStudent.id!);
              const sortedSchedules = studentSchedules.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              
              if (sortedSchedules.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    No sessions scheduled yet.
                  </div>
                );
              }
              
              return sortedSchedules.map(schedule => {
                const course = courses.find(c => c.id === schedule.courseId);
                const instructor = instructors.find(i => i.id === schedule.instructorId);
                const timeSlot = timeSlots.find(ts => ts.id === schedule.timeSlotId);
                
                return (
                  <Card key={schedule.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="font-medium">{course?.title || 'Unknown Course'}</div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <div>📅 {new Date(schedule.date).toLocaleDateString()}</div>
                            <div>🕐 {schedule.time}</div>
                            <div>👨‍🏫 {instructor?.firstName} {instructor?.lastName}</div>
                            {schedule.notes && <div>📝 {schedule.notes}</div>}
                          </div>
                        </div>
                        <Badge variant={
                          schedule.status === "completed" ? "default" :
                          schedule.status === "cancelled" ? "destructive" :
                          "secondary"
                        }>
                          {schedule.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowSessionsDialog(false)}>
              Close
            </Button>
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* Payment Dialog */}
      <UIDialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <UIDialogContent className="max-w-md">
          <UIDialogHeader>
            <UIDialogTitle>
              Add Payment for {selectedStudent?.firstName} {selectedStudent?.lastName}
            </UIDialogTitle>
          </UIDialogHeader>
          {selectedEnrollment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Course:</strong> {courses.find(c => c.id === selectedEnrollment.courseId)?.title || 'Unknown'}</div>
                  <div><strong>Course Fee:</strong> ₱{selectedEnrollment.courseFee}</div>
                  <div><strong>Amount Received:</strong> ₱{selectedEnrollment.amountReceived}</div>
                  <div className="text-destructive"><strong>Outstanding Balance:</strong> ₱{selectedEnrollment.balance}</div>
                </div>
              </div>
              
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div>
                  <Label htmlFor="paymentAmount">Payment Amount *</Label>
                  <Input
                    id="paymentAmount"
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: Number(e.target.value) })}
                    min="0.01"
                    max={selectedEnrollment.balance}
                    step="0.01"
                    required
                  />
                  <div className="text-sm text-muted-foreground mt-1">
                    New balance after payment: ₱{Math.max(0, selectedEnrollment.balance - paymentData.amount)}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="paymentMethod">Payment Method</Label>
                  <select
                    id="paymentMethod"
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    className="w-full border rounded p-2"
                  >
                    <option value="cash">Cash</option>
                    <option value="gcash">GCash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="paymentNotes">Notes (Optional)</Label>
                  <Input
                    id="paymentNotes"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Additional notes for this payment"
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processingPayment}>
                    {processingPayment ? "Recording..." : "Record Payment"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </UIDialogContent>
      </UIDialog>

      <MessageDialog 
        open={message.open} 
        onClose={() => setMessage({ ...message, open: false })} 
        message={message.text} 
        type={message.type} 
      />
    </div>
  );
};

export default StaffWalkInStudents;
